import { apiClient } from './api';
import { Order } from '../types/orders';
import { db } from './firebase';
import { collection, getDocs, getDoc, doc, query, orderBy, onSnapshot, where } from 'firebase/firestore';

export function subscribeToOrders(
    params: { status?: string; handoverStatus?: 'PENDING' | 'CONFIRMED' },
    onUpdate: (orders: Order[]) => void,
    onError: (error: any) => void
): () => void {
    let q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));

    if (params.status) {
        q = query(q, where('status', '==', params.status));
    }

    if (params.handoverStatus) {
        q = query(q, where('payment.method', '==', 'CASH_ON_DELIVERY'));
        q = query(q, where('payment.cash.handoverStatus', '==', params.handoverStatus));
    }

    return onSnapshot(
        q,
        (snapshot) => {
            const orders = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as any) as Order);
            onUpdate(orders);
        },
        (error) => {
            console.error("Error subscribing to orders:", error);
            onError(error);
        }
    );
}

export interface ListOrdersResponse {
    success: boolean;
    orders: Order[];
    pagination: {
        total: number;
        limit: number;
        offset: number;
        hasMore: boolean;
    };
}

// 1. Lectura Directa: Reemplaza /listOrders
export async function listOrders(params: {
    limit?: number;
    offset?: number;
    status?: string;
    search?: string
} = {}): Promise<ListOrdersResponse> {
    try {
        const limitVal = params.limit || 50;
        const offsetVal = params.offset || 0;

        // Construir query base
        let q = query(
            collection(db, 'orders'),
            orderBy('createdAt', 'desc')
        );

        // Filtro por estado (si se proporciona)
        if (params.status) {
            const { where } = await import('firebase/firestore');
            q = query(q, where('status', '==', params.status));
        }

        // Filtro por búsqueda de código (si se proporciona)
        // Nota: Firestore no soporta búsqueda parcial eficiente, solo igualdad exacta
        if (params.search) {
            const { where } = await import('firebase/firestore');
            q = query(q, where('orderCode', '==', params.search.toUpperCase()));
        }

        // Optimización: Solo traer los documentos necesarios
        const { limit: firestoreLimit } = await import('firebase/firestore');
        const fetchLimit = offsetVal + limitVal;
        q = query(q, firestoreLimit(fetchLimit));

        const snapshot = await getDocs(q);
        const allOrders = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as any)) as Order[];

        // Paginar en memoria solo los docs que trajimos
        const paginatedOrders = allOrders.slice(offsetVal);

        // Obtener conteo total usando getCountFromServer
        const { getCountFromServer } = await import('firebase/firestore');
        let countQuery = query(
            collection(db, 'orders'),
            orderBy('createdAt', 'desc')
        );
        if (params.status) {
            const { where } = await import('firebase/firestore');
            countQuery = query(countQuery, where('status', '==', params.status));
        }
        if (params.search) {
            const { where } = await import('firebase/firestore');
            countQuery = query(countQuery, where('orderCode', '==', params.search.toUpperCase()));
        }
        const countSnapshot = await getCountFromServer(countQuery);
        const totalCount = countSnapshot.data().count;

        return {
            success: true,
            orders: paginatedOrders,
            pagination: {
                total: totalCount,
                limit: limitVal,
                offset: offsetVal,
                hasMore: (offsetVal + paginatedOrders.length) < totalCount
            }
        };
    } catch (error) {
        console.error("Error listing orders:", error);
        return { success: false, orders: [], pagination: { total: 0, limit: 0, offset: 0, hasMore: false } };
    }
}

// 2. Lectura Directa: Reemplaza /getOrder/:id
export async function getOrder(orderId: string) {
    try {
        const docRef = doc(db, 'orders', orderId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return { success: true, order: { id: docSnap.id, ...docSnap.data() } as any as Order };
        }
        return { success: false, order: null as any };
    } catch (error) {
        console.error("Error getting order:", error);
        throw error;
    }
}

// 3. Lectura Directa (Privada): Reemplaza /getOrderPrivate/:id
export async function getOrderPrivate(orderId: string) {
    try {
        const docRef = doc(db, 'orders_private', orderId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return { success: true, orderPrivate: { id: docSnap.id, ...docSnap.data() } };
        }
        return { success: false, orderPrivate: null };
    } catch (error) {
        console.error("Error getting private order:", error);
        throw error;
    }
}

// --- ACCIONES DE ESCRITURA (SE QUEDAN EN BACKEND) ---

export async function cancelOrder(orderId: string, reason: string) {
    const response = await apiClient.post<{ success: boolean; message: string }>(`/cancelOrder/${orderId}`, { reason });
    return response.data;
}

export async function openOrderIncident(orderId: string, reason: string) {
    const response = await apiClient.post<{ success: boolean; message: string }>(`/openOrderIncident/${orderId}`, { reason });
    return response.data;
}

export async function resolveOrderIncident(orderId: string, resolutionType: 'RESUME' | 'DELIVER' | 'CANCEL', notes: string) {
    const response = await apiClient.post<{ success: boolean; message: string }>(`/resolveOrderIncident/${orderId}`, { resolutionType, notes });
    return response.data;
}

// --- LOGICA HIBRIDA DE INCIDENCIAS ---

export async function getIncidents(tab: 'OPEN' | 'REFUND_PENDING' | 'RESOLVED' = 'OPEN') {
    const response = await listOrders();

    const incidents = response.orders.filter(order => {
        if (!order.incident) return false;

        const isPaidDigital = order.payment.method === 'VPAY_SMARTLINK' && order.payment.status === 'PAID';

        if (tab === 'OPEN') {
            return order.incident.status === 'OPEN' && order.status !== 'Cancelado';
        }

        if (tab === 'REFUND_PENDING') {
            return order.incident.status === 'OPEN' && order.status === 'Cancelado' && isPaidDigital;
        }

        if (tab === 'RESOLVED') {
            if (order.incident.status === 'RESOLVED') return true;
            return order.status === 'Cancelado' && !isPaidDigital;
        }

        return false;
    });

    incidents.sort((a, b) => {
        const timeA = a.incident?.createdAt.seconds || 0;
        const timeB = b.incident?.createdAt.seconds || 0;
        return timeB - timeA;
    });

    return { ...response, orders: incidents };
}

export const ordersService = {
    listOrders,
    getOrder,
    getOrderPrivate,
    cancelOrder,
    openOrderIncident,
    resolveOrderIncident,
    getIncidents,
    subscribe: subscribeToOrders
};
